/**
 * bigflow.pairs Product Page — Main conversion page.
 *
 * Structure:
 * 1. Hero — Clear value proposition
 * 2. Before vs After — Visual comparison
 * 3. Features as Benefits — 6 benefit-driven cards
 * 4. Demo Visual — Store mockup with recommendations
 * 5. How It Works — 3 steps
 * 6. Pricing — Integrated with toggle (THE conversion point)
 * 7. FAQ — Consolidated from old Pricing + Product pages
 * 8. Final CTA
 */

import { Link } from 'react-router-dom'
import { useEffect, useState } from 'react'
import {
  ArrowRight,
  Check,
  X,
  Sparkles,
  Zap,
  Shield,
  BarChart3,
  Upload,
  ChevronDown,
  TrendingUp,
  RefreshCw,
  MousePointer,
} from 'lucide-react'

const APP_URL = import.meta.env.VITE_APP_URL || 'https://pairs.bigflowdata.com'

/* ---------------------------------------------------------------
   DATA
   --------------------------------------------------------------- */

const benefits = [
  {
    icon: BarChart3,
    title: 'Know exactly which products sell together',
    description:
      'Our analysis reveals the real purchasing patterns hidden in your order data — no spreadsheets, no guessing.',
  },
  {
    icon: Sparkles,
    title: 'Recommendations based on real behaviour',
    description:
      'Every suggestion is backed by actual customer purchases, not generic algorithms. Your store, your data, your edge.',
  },
  {
    icon: MousePointer,
    title: 'Live on your store in one click',
    description:
      'Push product recommendations directly to Shopify metafields. Your theme picks them up automatically.',
  },
  {
    icon: RefreshCw,
    title: 'Always up to date, automatically',
    description:
      'Schedule order syncs and re-analysis on your terms — hourly, daily, or weekly. Set it and forget it.',
  },
  {
    icon: Zap,
    title: 'Results in seconds, not hours',
    description:
      'Analyze thousands of orders in under 60 seconds. No waiting, no background jobs to babysit.',
  },
  {
    icon: Shield,
    title: 'Secure by default',
    description:
      'Your data is encrypted, processed securely, and never shared. GDPR compliant. Cancel anytime.',
  },
]

const plans = [
  {
    name: 'Basic',
    price: 39,
    priceAnnual: 390,
    description: 'Perfect for small to medium stores',
    features: [
      'Up to 1,000 orders analyzed',
      'Daily automatic sync',
      'Product association analysis',
      'Shopify metafield upload',
      'Email support',
    ],
    limitations: [],
  },
  {
    name: 'Pro',
    price: 79,
    priceAnnual: 790,
    description: 'For high-volume stores that need more',
    popular: true,
    features: [
      'Unlimited orders',
      'Hourly automatic sync',
      'Product association analysis',
      'Shopify metafield upload',
      'Priority support',
      'Advanced analytics',
    ],
    limitations: [],
  },
]

const faqs = [
  {
    q: 'How does the 15-day trial work?',
    a: "You choose a plan (Basic or Pro) and get full access to all features for 15 days. A payment method is required upfront. If you don't cancel before the trial ends, you'll be charged automatically.",
  },
  {
    q: 'What happens after my trial ends?',
    a: 'Your card will be charged for the selected plan. You can cancel anytime before the trial ends to avoid charges.',
  },
  {
    q: 'Can I upgrade or downgrade my plan?',
    a: 'Yes. Upgrades take effect immediately with pro-rated billing. Downgrades apply at the end of your billing period.',
  },
  {
    q: 'What happens to my data if I cancel?',
    a: 'Your recommendations stay in your Shopify store via metafields. We only store order data temporarily for analysis. After cancellation, we delete your data from our systems within 30 days.',
  },
  {
    q: 'How does the order limit work for Basic?',
    a: "The Basic plan analyzes your 1,000 most recent orders. If you have more, the older ones won't be included. The Pro plan has no limit.",
  },
  {
    q: 'Do I need to install anything on Shopify?',
    a: "Yes — you'll install our Shopify app which gives us permission to read your orders and write to metafields. It takes less than a minute.",
  },
  {
    q: 'What payment methods do you accept?',
    a: "We accept all major credit cards through Stripe. We don't store your card details — Stripe handles all payment processing securely.",
  },
  {
    q: 'Can I have multiple stores?',
    a: 'Yes! You can create up to 5 profiles, each connecting to a different Shopify store. Each profile requires its own subscription.',
  },
]

/* ---------------------------------------------------------------
   COMPONENT
   --------------------------------------------------------------- */

export function PairsProduct() {
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'annual'>('monthly')
  const [openFaq, setOpenFaq] = useState<number | null>(null)

  useEffect(() => {
    document.title = 'bigflow.pairs — Product Recommendations for Shopify'
  }, [])

  return (
    <div className="pt-16">
      {/* ============================================================
          HERO
          ============================================================ */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 dot-pattern opacity-50" />
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-brand-500/15 rounded-full blur-[128px]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 lg:px-8 py-24 lg:py-36">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-500/10 text-brand-500 text-sm font-medium mb-8 animate-fade-in">
              <Sparkles className="h-4 w-4" />
              Product Recommendations for Shopify
            </div>

            <h1
              className="text-4xl sm:text-5xl lg:text-7xl font-black mb-6 animate-fade-in leading-tight"
              style={{ animationDelay: '0.1s' }}
            >
              Stop guessing.
              <span className="gradient-text block">Start recommending.</span>
            </h1>

            <p
              className="text-lg lg:text-xl text-[var(--color-text-secondary)] mb-10 max-w-2xl mx-auto animate-fade-in"
              style={{ animationDelay: '0.2s' }}
            >
              Discover which products your customers buy together and turn
              that into revenue. Powered by your real order data.
            </p>

            <div
              className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in"
              style={{ animationDelay: '0.3s' }}
            >
              <a
                href="#pricing"
                className="inline-flex items-center gap-2 px-8 py-4 bg-brand-500 hover:bg-brand-600 text-white font-bold rounded-2xl transition-all hover:scale-105"
              >
                Start 15-Day Free Trial
                <ArrowRight className="h-5 w-5" />
              </a>
              <a
                href="#how-it-works"
                className="inline-flex items-center gap-2 px-8 py-4 bg-[var(--color-border)] hover:bg-[var(--color-text-muted)]/20 font-semibold rounded-2xl transition-all"
              >
                How It Works
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          BEFORE vs AFTER
          ============================================================ */}
      <section className="py-20 lg:py-28 section-alt">
        <div className="max-w-5xl mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              What changes with data-driven recommendations?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Without */}
            <div className="card-gradient rounded-2xl p-8 lg:p-10 border border-[var(--color-border)]">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-500/10 text-[var(--color-text-muted)] text-sm font-medium mb-6">
                Without bigflow.pairs
              </div>
              <ul className="space-y-4">
                {[
                  'Guessing which products to cross-sell',
                  'Manual CSV exports and spreadsheets',
                  'Generic "you might also like" widgets',
                  'No data on what actually works',
                  'Hours spent on analysis every week',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-[var(--color-text-muted)]">
                    <X className="h-5 w-5 text-[var(--color-text-muted)] flex-shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* With */}
            <div className="card-gradient rounded-2xl p-8 lg:p-10 border border-brand-500/30 ring-1 ring-brand-500/10">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-500/10 text-brand-500 text-sm font-medium mb-6">
                With bigflow.pairs
              </div>
              <ul className="space-y-4">
                {[
                  'Cross-sell backed by real purchase data',
                  'Fully automatic analysis, zero manual work',
                  '"Frequently Bought Together" from your data',
                  'Confidence scores for every product pair',
                  'Set up once, runs on autopilot',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-[var(--color-text-secondary)]">
                    <Check className="h-5 w-5 text-brand-500 flex-shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          FEATURES AS BENEFITS
          ============================================================ */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              Everything you need to sell smarter
            </h2>
            <p className="text-[var(--color-text-secondary)] text-lg max-w-2xl mx-auto">
              Powerful under the hood. Simple on the surface.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon
              return (
                <div
                  key={index}
                  className="card-gradient rounded-2xl p-6 border border-[var(--color-border)] hover:border-brand-500/30 transition-colors"
                >
                  <div className="w-12 h-12 rounded-xl bg-brand-500/10 flex items-center justify-center mb-4">
                    <Icon className="h-6 w-6 text-brand-500" />
                  </div>
                  <h3 className="text-lg font-bold mb-2">{benefit.title}</h3>
                  <p className="text-[var(--color-text-muted)] text-sm leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* ============================================================
          DEMO VISUAL — Store mockup
          ============================================================ */}
      <section className="py-20 lg:py-28 section-alt">
        <div className="max-w-5xl mx-auto px-4 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              See it in action
            </h2>
            <p className="text-[var(--color-text-secondary)] text-lg max-w-2xl mx-auto">
              This is how product recommendations look on your Shopify store.
            </p>
          </div>

          {/* Store Mockup */}
          <div className="card-gradient rounded-2xl border border-[var(--color-border)] overflow-hidden">
            {/* Browser bar */}
            <div className="flex items-center gap-2 px-4 py-3 border-b border-[var(--color-border)] bg-[var(--color-bg-secondary)]">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-500/60" />
                <div className="w-3 h-3 rounded-full bg-amber-500/60" />
                <div className="w-3 h-3 rounded-full bg-green-500/60" />
              </div>
              <div className="flex-1 text-center">
                <span className="text-xs text-[var(--color-text-muted)]">yourstore.myshopify.com/products/running-shoes</span>
              </div>
            </div>

            <div className="p-6 lg:p-10">
              {/* Product header */}
              <div className="grid lg:grid-cols-2 gap-8 mb-8">
                <div className="aspect-square bg-[var(--color-bg-secondary)] rounded-xl flex items-center justify-center">
                  <span className="text-6xl">👟</span>
                </div>
                <div className="flex flex-col justify-center">
                  <p className="text-sm text-[var(--color-text-muted)] mb-2">Your Brand</p>
                  <h3 className="text-2xl font-bold mb-2">Premium Running Shoes</h3>
                  <p className="text-xl text-brand-500 font-bold mb-4">€89.99</p>
                  <div className="w-full py-3 bg-[var(--color-bg-secondary)] rounded-xl text-center font-medium text-sm">
                    Add to Cart
                  </div>
                </div>
              </div>

              {/* Frequently Bought Together — powered by bigflow.pairs */}
              <div className="border-t border-[var(--color-border)] pt-8">
                <div className="flex items-center gap-2 mb-6">
                  <h4 className="text-lg font-bold">Frequently Bought Together</h4>
                  <span className="px-2 py-0.5 text-xs rounded-full bg-brand-500/10 text-brand-500 font-medium">
                    Powered by bigflow.pairs
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { name: 'Athletic Socks (3-Pack)', price: '€14.99', emoji: '🧦', confidence: '78%' },
                    { name: 'Sports Water Bottle', price: '€24.99', emoji: '🍶', confidence: '65%' },
                    { name: 'Running Shorts', price: '€39.99', emoji: '🩳', confidence: '61%' },
                    { name: 'Fitness Tracker Band', price: '€49.99', emoji: '⌚', confidence: '54%' },
                  ].map((product, i) => (
                    <div key={i} className="bg-[var(--color-bg-secondary)] rounded-xl p-4 border border-[var(--color-border)]">
                      <div className="aspect-square bg-[var(--color-bg)] rounded-lg flex items-center justify-center mb-3">
                        <span className="text-3xl">{product.emoji}</span>
                      </div>
                      <p className="text-sm font-medium mb-1 truncate">{product.name}</p>
                      <p className="text-sm text-brand-500 font-bold">{product.price}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          HOW IT WORKS — 3 steps
          ============================================================ */}
      <section id="how-it-works" className="py-20 lg:py-28 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              Up and running in minutes
            </h2>
            <p className="text-[var(--color-text-secondary)] text-lg max-w-2xl mx-auto">
              Three simple steps to data-driven product recommendations.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            {[
              {
                step: '01',
                title: 'Connect your store',
                desc: 'Install our Shopify app and authorize access. We import your order history automatically.',
                icon: Upload,
              },
              {
                step: '02',
                title: 'Discover patterns',
                desc: 'Our engine analyzes thousands of orders in seconds and finds which products are bought together.',
                icon: BarChart3,
              },
              {
                step: '03',
                title: 'Boost your sales',
                desc: 'Push recommendations to your Shopify metafields with one click. Your theme displays them instantly.',
                icon: TrendingUp,
              },
            ].map((item, index) => {
              const Icon = item.icon
              return (
                <div key={index} className="text-center relative">
                  {index < 2 && (
                    <div className="hidden md:block absolute top-10 left-[60%] w-[80%] h-0.5 bg-[var(--color-border)]" />
                  )}
                  <div className="relative z-10 w-20 h-20 mx-auto mb-6 rounded-2xl bg-brand-500/10 flex items-center justify-center border-2 border-[var(--color-border)]">
                    <Icon className="h-8 w-8 text-brand-500" />
                  </div>
                  <div className="text-sm text-brand-500 font-bold mb-2">Step {item.step}</div>
                  <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                  <p className="text-[var(--color-text-secondary)]">{item.desc}</p>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* ============================================================
          PRICING — THE CONVERSION POINT
          ============================================================ */}
      <section id="pricing" className="py-20 lg:py-28 section-alt scroll-mt-20">
        <div className="max-w-5xl mx-auto px-4 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              Simple, transparent pricing
            </h2>
            <p className="text-[var(--color-text-secondary)] text-lg mb-8">
              Choose the plan that fits your store. 15-day free trial on all plans.
            </p>

            {/* Billing Toggle */}
            <div className="inline-flex items-center gap-4 p-1 rounded-xl bg-[var(--color-bg)]">
              <button
                onClick={() => setBillingInterval('monthly')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  billingInterval === 'monthly'
                    ? 'bg-brand-500 text-white'
                    : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text)]'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingInterval('annual')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  billingInterval === 'annual'
                    ? 'bg-brand-500 text-white'
                    : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text)]'
                }`}
              >
                Annual <span className="text-xs opacity-75">(2 months free)</span>
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12 items-stretch">
            {plans.map((plan, index) => (
              <div
                key={index}
                className={`card-gradient rounded-2xl p-8 border transition-all flex flex-col ${
                  plan.popular
                    ? 'border-brand-500 ring-2 ring-brand-500/20'
                    : 'border-[var(--color-border)]'
                }`}
              >
                {plan.popular ? (
                  <div className="inline-flex px-3 py-1 rounded-full bg-brand-500 text-white text-xs font-bold mb-4 self-start">
                    MOST POPULAR
                  </div>
                ) : (
                  <div className="mb-4 h-[26px]" />
                )}
                <h3 className="text-2xl font-bold mb-1">{plan.name}</h3>
                <p className="text-[var(--color-text-muted)] text-sm mb-4">{plan.description}</p>
                <div className="flex items-baseline gap-1 mb-2">
                  <span className="text-4xl font-black">
                    €{billingInterval === 'monthly' ? plan.price : Math.round(plan.priceAnnual / 12)}
                  </span>
                  <span className="text-[var(--color-text-muted)]">/month</span>
                </div>
                {billingInterval === 'annual' && (
                  <p className="text-sm text-brand-500 mb-4">
                    Billed €{plan.priceAnnual}/year, save €{plan.price * 12 - plan.priceAnnual}
                  </p>
                )}
                {billingInterval === 'monthly' && <div className="mb-4" />}
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-3 text-sm">
                      <Check className="h-5 w-5 text-brand-500 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <div className="mt-auto">
                  <a
                    href={`${APP_URL}/login?plan=${plan.name.toLowerCase()}&interval=${billingInterval}`}
                    className={`block w-full py-3 rounded-xl font-semibold text-center transition-colors ${
                      plan.popular
                        ? 'bg-brand-500 hover:bg-brand-600 text-white'
                        : 'bg-[var(--color-border)] hover:bg-[var(--color-text-muted)]/30'
                    }`}
                  >
                    Start 15-Day Free Trial
                  </a>
                  <p className="text-center text-xs text-[var(--color-text-muted)] mt-3">
                    Card required · Cancel anytime
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Feature Comparison Table */}
          <div className="card-gradient rounded-2xl border border-[var(--color-border)] overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border)]">
                  <th className="text-left p-4 font-semibold">Feature</th>
                  <th className="text-center p-4 font-semibold">Basic</th>
                  <th className="text-center p-4 font-semibold bg-brand-500/5">Pro</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { feature: 'Order limit', basic: '1,000', pro: 'Unlimited', highlight: true },
                  { feature: 'Sync frequency', basic: 'Daily', pro: 'Hourly', highlight: true },
                  { feature: 'Product association analysis', basic: true, pro: true },
                  { feature: 'Shopify metafield upload', basic: true, pro: true },
                  { feature: 'Scheduled automation', basic: true, pro: true },
                  { feature: 'Support', basic: 'Email', pro: 'Priority', highlight: true },
                  { feature: 'Advanced analytics', basic: false, pro: true },
                ].map((row, i) => (
                  <tr key={i} className="border-b border-[var(--color-border)] last:border-0">
                    <td className="p-4 text-[var(--color-text-secondary)]">{row.feature}</td>
                    <td className="p-4 text-center">
                      {typeof row.basic === 'boolean' ? (
                        row.basic ? (
                          <Check className="h-5 w-5 text-brand-500 mx-auto" />
                        ) : (
                          <span className="text-[var(--color-text-muted)]">—</span>
                        )
                      ) : (
                        row.basic
                      )}
                    </td>
                    <td className={`p-4 text-center bg-brand-500/5 ${row.highlight ? 'text-brand-500 font-medium' : ''}`}>
                      {typeof row.pro === 'boolean' ? (
                        row.pro ? (
                          <Check className="h-5 w-5 text-brand-500 mx-auto" />
                        ) : (
                          <span className="text-[var(--color-text-muted)]">—</span>
                        )
                      ) : (
                        row.pro
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ============================================================
          FAQ
          ============================================================ */}
      <section className="py-20 lg:py-28">
        <div className="max-w-3xl mx-auto px-4 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              Frequently asked questions
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="card-gradient rounded-xl border border-[var(--color-border)] overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left"
                >
                  <span className="font-medium">{faq.q}</span>
                  <ChevronDown
                    className={`h-5 w-5 text-[var(--color-text-muted)] transition-transform flex-shrink-0 ml-4 ${
                      openFaq === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-4 text-[var(--color-text-secondary)]">{faq.a}</div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-8 text-center">
            <p className="text-[var(--color-text-muted)]">
              Have more questions?{' '}
              <Link to="/contact" className="text-brand-500 hover:underline">
                Contact us
              </Link>
            </p>
          </div>
        </div>
      </section>

      {/* ============================================================
          FINAL CTA
          ============================================================ */}
      <section className="py-20 lg:py-28 section-alt">
        <div className="max-w-4xl mx-auto px-4 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Ready to let your data do the selling?
          </h2>
          <p className="text-xl text-[var(--color-text-secondary)] mb-10">
            Start your 15-day free trial today. No risk, cancel anytime.
          </p>
          <a
            href={`${APP_URL}/login`}
            className="inline-flex items-center gap-2 px-8 py-4 bg-brand-500 hover:bg-brand-600 text-white font-bold rounded-2xl transition-all hover:scale-105"
          >
            Start Free Trial
            <ArrowRight className="h-5 w-5" />
          </a>
          <p className="mt-4 text-sm text-[var(--color-text-muted)]">
            15-day free trial · Card required · Cancel anytime
          </p>
        </div>
      </section>
    </div>
  )
}
